import { useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { Ellipsis, Keyboard, LogOut, Moon, Sun, X } from "lucide-react";
import { cn } from "@web/lib/cn";
import { useLiveStatus } from "@web/lib/events";
import { useSession } from "@web/lib/queries";
import { useTheme } from "@web/lib/theme";
import { Modal } from "@web/ui/Modal";
import { Shortcut } from "@web/ui/Kbd";
import { runCommand } from "./commands";
import {
  CLAUDE_MENU_LINKS,
  isNavActive,
  PHONE_MORE,
  PHONE_MORE_PUT_AWAY,
  PHONE_TABS,
  PUT_AWAY_LABEL,
  SIDEBAR_MAIN,
  SIDEBAR_PUT_AWAY,
  type ClaudeMenuLink,
  type NavItem,
} from "./layout";

function LiveDot() {
  const status = useLiveStatus();
  const label =
    status === "live"
      ? "Up to date"
      : status === "connecting"
        ? "Connecting…"
        : "Offline, retrying";
  return (
    <span className="flex items-center gap-2 text-xs text-muted" title={label}>
      <span
        aria-hidden
        className={cn("size-1.5 rounded-full", status === "live" ? "bg-calm" : "bg-faint")}
      />
      {label}
    </span>
  );
}

export function Sidebar({ pathname }: { pathname: string }) {
  const session = useSession();
  const { resolved } = useTheme();
  return (
    <nav
      aria-label="Main"
      className="hidden w-60 shrink-0 flex-col border-r border-line bg-bg px-3 py-4 md:flex"
    >
      <Link to="/feed" className="mb-6 px-2 font-serif text-xl font-semibold text-ink">
        MeeMail
      </Link>
      <ul className="flex flex-col gap-0.5">
        {SIDEBAR_MAIN.map((item) => (
          <SidebarLink key={item.to} item={item} pathname={pathname} />
        ))}
      </ul>
      <section aria-labelledby="nav-put-away" className="mt-6">
        <h2 id="nav-put-away" className="mm-kicker mb-1 px-2">
          {PUT_AWAY_LABEL}
        </h2>
        <ul className="flex flex-col gap-0.5">
          {SIDEBAR_PUT_AWAY.map((item) => (
            <SidebarLink key={item.to} item={item} pathname={pathname} small />
          ))}
        </ul>
      </section>

      <div className="mt-auto flex flex-col gap-1 px-1">
        <LiveDot />
        {session.data?.email ? (
          <p className="truncate text-xs text-faint" title={session.data.email}>
            {session.data.displayName || session.data.email}
          </p>
        ) : null}
        <div className="mt-2 flex items-center gap-1">
          <IconButton
            label={resolved === "dark" ? "Switch to light" : "Switch to dark"}
            onClick={() => runCommand("theme.toggle")}
          >
            {resolved === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
          </IconButton>
          <IconButton label="Keyboard shortcuts (?)" onClick={() => runCommand("help.shortcuts")}>
            <Keyboard className="size-4" />
          </IconButton>
          <IconButton label="Log out" onClick={() => runCommand("session.logout")}>
            <LogOut className="size-4" />
          </IconButton>
        </div>
      </div>
    </nav>
  );
}

function SidebarLink({
  item,
  pathname,
  small = false,
}: {
  item: NavItem;
  pathname: string;
  /** The muted "Put away" group: smaller and quieter than the main places. */
  small?: boolean;
}) {
  const active = isNavActive(pathname, item);
  const Icon = item.icon;
  return (
    <li>
      <Link
        to={item.to}
        aria-current={active ? "page" : undefined}
        className={cn(
          "group flex items-center gap-2.5 rounded-lg px-2",
          small ? "h-8 text-[13px]" : "h-9 text-[15px]",
          active
            ? "bg-paper font-medium text-ink shadow-soft"
            : small
              ? "text-muted hover:bg-sunk hover:text-ink"
              : "text-ink-2 hover:bg-sunk",
        )}
      >
        <Icon className={cn("shrink-0 text-muted", small ? "size-3.5" : "size-4")} />
        <span className="flex-1 whitespace-nowrap">{item.label}</span>
        <Shortcut
          binding={item.binding}
          className="opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
        />
      </Link>
    </li>
  );
}

function IconButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="inline-flex size-8 items-center justify-center rounded-lg text-muted hover:bg-sunk hover:text-ink"
    >
      {children}
    </button>
  );
}

const TAB_CLASS = "flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 text-[11px]";

/** Phone-width navigation: Feed, Inbox and Tasks as tabs; everything else opens from More. */
export function BottomNav({ pathname }: { pathname: string }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const moreActive = [...PHONE_MORE, ...PHONE_MORE_PUT_AWAY, ...CLAUDE_MENU_LINKS].some((item) =>
    isNavActive(pathname, item),
  );
  return (
    <>
      <nav
        aria-label="Main"
        className="flex h-14 shrink-0 items-stretch border-t border-line bg-paper pb-[env(safe-area-inset-bottom)] md:hidden"
      >
        {PHONE_TABS.map((item) => {
          const active = isNavActive(pathname, item);
          const Icon = item.icon;
          return (
            <Link
              key={item.to}
              to={item.to}
              aria-current={active ? "page" : undefined}
              className={cn(TAB_CLASS, active ? "text-accent-text" : "text-muted")}
            >
              <Icon className="size-5" />
              {item.label}
            </Link>
          );
        })}
        <button
          type="button"
          onClick={() => setMoreOpen(true)}
          aria-haspopup="dialog"
          className={cn(TAB_CLASS, moreActive ? "text-accent-text" : "text-muted")}
        >
          <Ellipsis className="size-5" />
          More
        </button>
      </nav>
      {/* Outside the nav, which is display:none on wide screens. */}
      <MoreSheet open={moreOpen} pathname={pathname} onClose={() => setMoreOpen(false)} />
    </>
  );
}

/** The rest of the destinations, as a bottom sheet. Picking one closes it. */
function MoreSheet({
  open,
  pathname,
  onClose,
}: {
  open: boolean;
  pathname: string;
  onClose: () => void;
}) {
  const item = (link: NavItem | ClaudeMenuLink) => {
    const active = isNavActive(pathname, link);
    const Icon = link.icon;
    return (
      <li key={link.to}>
        <Link
          to={link.to}
          onClick={onClose}
          aria-current={active ? "page" : undefined}
          className={cn(
            "flex h-12 items-center gap-3 rounded-lg px-3 text-[15px]",
            active ? "bg-accent-soft font-medium text-ink" : "text-ink-2 hover:bg-sunk",
          )}
        >
          <Icon className="size-5 text-muted" />
          {link.label}
        </Link>
      </li>
    );
  };
  return (
    <Modal open={open} onClose={onClose} label="More" className="mm-sheet">
      <div className="flex items-center justify-between px-5 pt-4 pb-1">
        <h2 className="text-lg">More</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="rounded-md p-1 text-muted hover:bg-sunk hover:text-ink"
        >
          <X className="size-4" />
        </button>
      </div>
      <ul className="flex flex-col px-2 pb-2">{PHONE_MORE.map(item)}</ul>
      <section aria-labelledby="more-put-away" className="mx-2 border-t border-line pt-2 pb-2">
        <h3 id="more-put-away" className="mm-kicker px-3 pb-1">
          {PUT_AWAY_LABEL}
        </h3>
        <ul className="flex flex-col">{PHONE_MORE_PUT_AWAY.map(item)}</ul>
      </section>
      <section aria-labelledby="more-claude" className="mx-2 border-t border-line pt-2 pb-2">
        <h3 id="more-claude" className="mm-kicker px-3 pb-1">
          Claude
        </h3>
        <ul className="flex flex-col">{CLAUDE_MENU_LINKS.map(item)}</ul>
      </section>
      <div className="border-t border-line px-5 pt-3 pb-[max(12px,env(safe-area-inset-bottom))]">
        <LiveDot />
      </div>
    </Modal>
  );
}
